import { useEffect, useState } from 'react'

/**
 * An online read keyed by its inputs: `data` and `error` belong to the key
 * they were read for, so a new key shows nothing stale and no state is set
 * during an effect (only when the read answers). `reload` asks again.
 */
export const useAsync = <T>(key: string | null, load: () => Promise<T>) => {
  const [res, setRes] = useState<{ key: string; n: number; data?: T; error?: string } | null>(null)
  const [n, setN] = useState(0)
  useEffect(() => {
    if (key === null) return
    let stale = false
    load()
      .then((data) => !stale && setRes({ key, n, data }))
      .catch((e: Error) => !stale && setRes({ key, n, error: e.message || 'it did not answer' }))
    return () => {
      stale = true
    }
    // load is rebuilt each render; the key is what it reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, n])
  const mine = res && res.key === key ? res : null
  return {
    data: mine?.data,
    error: mine?.error,
    /** a read for this key is on its way and nothing has answered yet */
    loading: key !== null && !mine,
    reload: () => setN((x) => x + 1),
  }
}
